import axios, { AxiosHeaders, AxiosRequestConfig } from 'axios'

import { tagRequestId } from '../observability/sentry'

const axiosInstance = axios.create()

function apiErrorMessage(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null

  const data = error.response?.data
  if (data && typeof data === 'object' && 'detail' in data) {
    const detail = (data as { detail?: unknown }).detail
    if (typeof detail === 'string' && detail.trim()) return detail
    if (
      detail &&
      typeof detail === 'object' &&
      'message' in detail &&
      typeof (detail as { message?: unknown }).message === 'string' &&
      (detail as { message: string }).message.trim()
    ) {
      return (detail as { message: string }).message
    }
    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => {
          if (item && typeof item === 'object' && 'msg' in item) {
            return String((item as { msg?: unknown }).msg ?? '')
          }
          return String(item ?? '')
        })
        .filter(Boolean)
      if (messages.length > 0) return messages.join('; ')
    }
  }
  if (typeof data === 'string' && data.trim()) return data.trim()
  if (error.response?.status) return `Erro ${error.response.status} na API.`
  return error.message || null
}

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('idf_token')
  if (token) {
    const headers = (config.headers ?? new AxiosHeaders()) as AxiosHeaders
    headers.set('Authorization', `Bearer ${token}`)
    config.headers = headers
  }
  return config
})

axiosInstance.interceptors.response.use(
  (response) => {
    // Surface the server's X-Request-Id onto Sentry so any error captured in
    // the same tick correlates with the server's log entry.
    const requestId = response.headers?.['x-request-id']
    if (typeof requestId === 'string') tagRequestId(requestId)
    return response
  },
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem('idf_token')
      localStorage.removeItem('idf_user')
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login'
      }
    }
    const errRequestId = error?.response?.headers?.['x-request-id']
    if (typeof errRequestId === 'string') tagRequestId(errRequestId)
    const message = apiErrorMessage(error)
    if (message && error instanceof Error) {
      error.message = message
    }
    return Promise.reject(error)
  },
)

export const apiClient = async <T>(config: AxiosRequestConfig): Promise<T> => {
  const source = axios.CancelToken.source()
  const promise = axiosInstance({ ...config, cancelToken: source.token }).then(
    ({ data }) => data,
  )
  // Orval signal support: allow aborting via AbortController
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(promise as any).cancel = () => {
    source.cancel('Query was cancelled')
  }
  return promise
}

export default apiClient
